/**
 * Where the @mention picker should be looking, given a draft and a caret position.
 *
 * The composer used to answer this with `input.lastIndexOf('@')`, which is wrong in
 * three ways that all show up in ordinary use:
 *
 * - It ignores the caret. Going back to fix a typo earlier in the message kept the
 *   picker pointed at the LAST `@` in the draft, so the accepted suggestion was
 *   spliced in at the wrong end of the sentence.
 * - It ignores word boundaries, so `dev@synapse.io` opened the picker.
 * - It has no terminator, so the "query" ran to the end of the draft. After
 *   `@Mia are you around`, the query was `Mia are you around` — matching nobody, and
 *   the panel only stayed hidden because an empty result list happens to hide it.
 *
 * A mention token is: an `@` at a word boundary, followed by run of non-whitespace
 * that the caret sits inside or immediately after. A space closes it — which also
 * means accepting a suggestion (which appends a space) closes the picker, exactly as
 * it should.
 *
 * @param {string} text  the draft
 * @param {number} caret caret offset; `text.length` is the normal typing case
 * @returns {{ at: number, query: string } | null} `at` is the index of the `@`
 */

// Matches `NAME_CHAR` in ./index.js — the two have to agree on what a name is.
const NAME_CHAR = /[\p{L}\p{N}_]/u;

/** Longest name we will try to complete. Guards against scanning a pasted essay. */
const MAX_QUERY = 64;

export const findMentionQuery = (text, caret) => {
  if (!text) return null;

  const cursor = Number.isInteger(caret) ? Math.min(Math.max(caret, 0), text.length) : text.length;
  const from = Math.max(0, cursor - MAX_QUERY - 1);

  for (let i = cursor - 1; i >= from; i -= 1) {
    const char = text[i];

    // Whitespace closes the token: an `@` before this point belongs to an earlier word.
    if (/\s/.test(char)) return null;

    if (char === '@') {
      // Word boundary — `dev@synapse.io` is an address, not an address-of.
      const before = text[i - 1];
      if (before !== undefined && NAME_CHAR.test(before)) return null;
      return { at: i, query: text.slice(i + 1, cursor) };
    }
  }

  return null;
};

/**
 * Splice an accepted suggestion into the draft.
 *
 * Returns the new text and where the caret should land, so the caller does not have
 * to recompute either. The trailing space is what closes the picker — see above — and
 * it is what the author would type next anyway.
 *
 * Only the token is replaced, not everything from the `@` to the end of the draft:
 * completing a mention mid-sentence used to truncate the rest of the message.
 */
export const applyMention = (text, at, caret, name) => {
  const head = text.slice(0, at);
  const tail = text.slice(Math.min(Math.max(caret, at), text.length));
  const inserted = `@${name} `;
  // Don't double the separator if the author already left a space after the caret.
  const joined = `${head}${inserted}${tail.startsWith(' ') ? tail.slice(1) : tail}`;
  return { text: joined, caret: head.length + inserted.length };
};

export default { findMentionQuery, applyMention };
